import { CONFIGS, useConfigQuery } from '@buildwise/libs/configurations';
import { Autocomplete, FormControl, TextField, Grid } from '@mui/material';
import { DEFAULT_GPT_CHAT_SERVICE_PROVIDER } from 'components/utils/constants';
import { BUILDWISE_GPT_CHAT } from 'constants/routes';
import React, { SyntheticEvent, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import _ from 'lodash';
import useUserData from 'hooks/useUserData';
import { deleteChatFromSessionStorage, getChatFromSessionStorage } from 'utility/helpers';

export type BedrockFormData = {
  llm_model_name: { label: string; value: string; provider: string } | null;
  token_size: string;
};

type BuiidWiseGPTFormProps = {
  onNewSession?: () => void;
  setServiceProvider?: React.Dispatch<React.SetStateAction<string | null>>;
};

const BuildWiseGPTForm = ({ onNewSession, setServiceProvider }: BuiidWiseGPTFormProps) => {
  const { activeUniqueEntries } = useConfigQuery({
    configName: CONFIGS.BW_AI_MODELS.configName,
  });
  const { companyId } = useUserData();
  const { pathname } = useLocation();
  const sessionKeyName = useMemo(() => {
    return BUILDWISE_GPT_CHAT.includes(pathname) ? 'bedrockChatForm' : 'bedrockTextForm';
  }, [pathname]);

  const llmModelOptions = useMemo(
    () =>
      activeUniqueEntries.map((entry) => ({
        label: entry.entryDescription || entry.entryCode,
        value: entry.entryCode,
        provider: entry.otherAttributes?.provider || DEFAULT_GPT_CHAT_SERVICE_PROVIDER,
      })),
    [activeUniqueEntries],
  );
  const defaultFormState: BedrockFormData = {
    ...(JSON.parse(String(sessionStorage.getItem(sessionKeyName))) || {
      llm_model_name: llmModelOptions[0],
      token_size: '5000',
    }),
  };
  const { control, setValue, watch, reset } = useForm<BedrockFormData>({
    mode: 'all',
    defaultValues: defaultFormState,
  });
  const watchedValues = watch(['llm_model_name', 'token_size']);

  useEffect(() => {
    if (llmModelOptions.length) {
      reset(
        !!defaultFormState?.llm_model_name
          ? defaultFormState
          : {
              llm_model_name: llmModelOptions[0],
              token_size: '5000',
            },
        {
          keepDirty: true,
        },
      );
    }
  }, [llmModelOptions, pathname]);

  useEffect(() => {
    sessionStorage.setItem(
      sessionKeyName,
      JSON.stringify({ llm_model_name: watchedValues[0], token_size: watchedValues[1] }),
    );
    setServiceProvider?.(watchedValues[0]?.provider || null);
  }, [watchedValues]);

  const handleChange = (value) => {
    setValue('llm_model_name', value, {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (BUILDWISE_GPT_CHAT.includes(pathname)) {
      const isSameSerivceProvider =
        JSON.parse(String(sessionStorage.getItem(sessionKeyName)))?.llm_model_name?.provider ===
        value?.provider;

      const prevHistory = _.orderBy(
        getChatFromSessionStorage({ companyId, key: 'bedrockChat' }),
        'createdAt',
      );

      if (!isSameSerivceProvider && prevHistory?.length > 0) {
        toast.info('The chat history has been cleared because the LLM provider has been changed.');
        onNewSession?.();
      }
    }
  };

  return (
    <>
      <Grid container spacing={1}>
        <Grid item sm={12} lg={6}>
          <Controller
            control={control}
            render={({ field: { ref, ...field }, fieldState }) => (
              <FormControl fullWidth>
                <Autocomplete
                  {...field}
                  autoHighlight
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  onChange={(_: SyntheticEvent<Element>, value) => {
                    handleChange(value);
                  }}
                  options={llmModelOptions}
                  noOptionsText={`No llm models Found`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!fieldState.error}
                      helperText={fieldState?.error?.message || ''}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      placeholder="Select LLM model"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          padding: '0px 8px',
                        },
                      }}
                    />
                  )}
                  disabled={false}
                />
              </FormControl>
            )}
            name="llm_model_name"
          />
        </Grid>
        <Grid item sm={12} lg={6}>
          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <FormControl fullWidth>
                <TextField
                  {...field}
                  value={!!field.value.trim() ? field.value : ''}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message || ''}
                  fullWidth
                  placeholder="Please enter token size"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  disabled={false}
                  sx={{
                    '& .MuiOutlinedInput-input': {
                      padding: '8px 10px',
                    },
                  }}
                />
              </FormControl>
            )}
            name="token_size"
          />
        </Grid>
      </Grid>
    </>
  );
};

export default BuildWiseGPTForm;
